import React, { useState, useEffect } from 'react'
import { apiService } from '../services/api'
import {
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline'

interface Payment {
  id: string
  payment_number: string
  payment_date: string
  payment_type: 'receipt' | 'payment'
  payment_category: 'vendor' | 'customer' | 'expense' | 'income' | 'refund'
  source_transaction_id?: string
  source_transaction_type?: string
  amount: number
  tax_amount: number
  total_amount: number
  payment_method: 'cash' | 'bank' | 'card' | 'digital_wallet' | 'bank_transfer'
  payment_reference?: string
  bank_account_id?: string
  description?: string
  notes?: string
  is_reconciled: boolean
  reconciled_at?: string
  reconciled_by?: string
  created_at: string
  updated_at: string
  created_by?: string
}

const Payments: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [statusModalMessage, setStatusModalMessage] = useState('')
  const [statusModalType, setStatusModalType] = useState<'success' | 'error'>('success')
  const [isEditing, setIsEditing] = useState(false)
  
  // Filters
  const [filters, setFilters] = useState({
    payment_type: 'all',
    payment_category: 'all',
    payment_method: 'all',
    start_date: '',
    end_date: '',
    search: '',
    is_reconciled: 'all'
  })

  // Form state
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_type: 'receipt' as 'receipt' | 'payment',
    payment_category: 'customer' as 'vendor' | 'customer' | 'expense' | 'income' | 'refund',
    source_transaction_id: '',
    source_transaction_type: '',
    amount: '',
    tax_amount: '',
    payment_method: 'cash' as 'cash' | 'bank' | 'card' | 'digital_wallet' | 'bank_transfer',
    payment_reference: '',
    bank_account_id: '',
    description: '',
    notes: ''
  })

  useEffect(() => {
    loadPayments()
  }, [filters])

  const loadPayments = async () => {
    setLoading(true)
    try {
      const filterParams: any = {}
      if (filters.payment_type !== 'all') filterParams.payment_type = filters.payment_type
      if (filters.payment_category !== 'all') filterParams.payment_category = filters.payment_category
      if (filters.payment_method !== 'all') filterParams.payment_method = filters.payment_method
      if (filters.start_date) filterParams.start_date = filters.start_date
      if (filters.end_date) filterParams.end_date = filters.end_date
      if (filters.search) filterParams.search = filters.search
      if (filters.is_reconciled !== 'all') filterParams.is_reconciled = filters.is_reconciled === 'true'

      const response = await apiService.getPayments(filterParams)
      if (response.success && response.data) {
        setPayments(response.data as Payment[])
      }
    } catch (error) {
      console.error('Error loading payments:', error)
      showStatusMessage('Failed to load payments', 'error')
    } finally {
      setLoading(false)
    }
  }

  const showStatusMessage = (message: string, type: 'success' | 'error') => {
    setStatusModalMessage(message)
    setStatusModalType(type)
    setShowStatusModal(true)
  }

  const formatCurrency = (amount: number | string | null | undefined) => {
    if (amount === null || amount === undefined) {
      return 'SAR 0.00'
    }
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    if (isNaN(numAmount)) {
      return 'SAR 0.00'
    }
    return `SAR ${numAmount.toFixed(2)}`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getPaymentTypeBadge = (type: string) => {
    if (type === 'receipt') {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
          Receipt
        </span>
      )
    } else {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
          Payment
        </span>
      )
    }
  }

  const getPaymentCategoryBadge = (category: string) => {
    const colors: Record<string, { bg: string; text: string; darkBg: string; darkText: string }> = {
      vendor: { bg: 'bg-blue-100', text: 'text-blue-800', darkBg: 'dark:bg-blue-900', darkText: 'dark:text-blue-200' },
      customer: { bg: 'bg-purple-100', text: 'text-purple-800', darkBg: 'dark:bg-purple-900', darkText: 'dark:text-purple-200' },
      expense: { bg: 'bg-orange-100', text: 'text-orange-800', darkBg: 'dark:bg-orange-900', darkText: 'dark:text-orange-200' },
      income: { bg: 'bg-green-100', text: 'text-green-800', darkBg: 'dark:bg-green-900', darkText: 'dark:text-green-200' },
      refund: { bg: 'bg-yellow-100', text: 'text-yellow-800', darkBg: 'dark:bg-yellow-900', darkText: 'dark:text-yellow-200' }
    }
    const color = colors[category] || colors.customer
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${color.bg} ${color.text} ${color.darkBg} ${color.darkText}`}>
        {category.charAt(0).toUpperCase() + category.slice(1)}
      </span>
    )
  }

  const handleCreatePayment = () => {
    setIsEditing(false)
    setPaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      payment_type: 'receipt',
      payment_category: 'customer',
      source_transaction_id: '',
      source_transaction_type: '',
      amount: '',
      tax_amount: '',
      payment_method: 'cash',
      payment_reference: '',
      bank_account_id: '',
      description: '',
      notes: ''
    })
    setSelectedPayment(null)
    setShowPaymentModal(true)
  }

  const handleEditPayment = (payment: Payment) => {
    setIsEditing(true)
    setSelectedPayment(payment)
    setPaymentForm({
      payment_date: payment.payment_date,
      payment_type: payment.payment_type,
      payment_category: payment.payment_category,
      source_transaction_id: payment.source_transaction_id || '',
      source_transaction_type: payment.source_transaction_type || '',
      amount: payment.amount.toString(),
      tax_amount: payment.tax_amount.toString(),
      payment_method: payment.payment_method,
      payment_reference: payment.payment_reference || '',
      bank_account_id: payment.bank_account_id || '',
      description: payment.description || '',
      notes: payment.notes || ''
    })
    setShowPaymentModal(true)
  }

  const handleViewPayment = (payment: Payment) => {
    setSelectedPayment(payment)
    setShowPaymentModal(true)
  }

  const handleDeletePayment = (payment: Payment) => {
    setSelectedPayment(payment)
    setShowDeleteModal(true)
  }

  const handleSavePayment = async () => {
    if (!paymentForm.payment_date || !paymentForm.amount || !paymentForm.payment_method) {
      showStatusMessage('Please fill in all required fields', 'error')
      return
    }

    try {
      const paymentData = {
        payment_date: paymentForm.payment_date,
        payment_type: paymentForm.payment_type,
        payment_category: paymentForm.payment_category,
        source_transaction_id: paymentForm.source_transaction_id || null,
        source_transaction_type: paymentForm.source_transaction_type || null,
        amount: parseFloat(paymentForm.amount),
        tax_amount: parseFloat(paymentForm.tax_amount || '0'),
        payment_method: paymentForm.payment_method,
        payment_reference: paymentForm.payment_reference || null,
        bank_account_id: paymentForm.bank_account_id || null,
        description: paymentForm.description || null,
        notes: paymentForm.notes || null
      }

      let response
      if (isEditing && selectedPayment) {
        response = await apiService.updatePayment(selectedPayment.id, paymentData)
      } else {
        response = await apiService.createPayment(paymentData)
      }

      if (response.success) {
        showStatusMessage(
          isEditing ? 'Payment updated successfully' : 'Payment created successfully',
          'success'
        )
        setShowPaymentModal(false)
        loadPayments()
      } else {
        showStatusMessage(response.error || 'Failed to save payment', 'error')
      }
    } catch (error) {
      console.error('Error saving payment:', error)
      showStatusMessage('Failed to save payment', 'error')
    }
  }

  const confirmDeletePayment = async () => {
    if (!selectedPayment) return

    try {
      const response = await apiService.deletePayment(selectedPayment.id)
      if (response.success) {
        showStatusMessage('Payment deleted successfully', 'success')
        setShowDeleteModal(false)
        setSelectedPayment(null)
        loadPayments()
      } else {
        showStatusMessage(response.error || 'Failed to delete payment', 'error')
      }
    } catch (error) {
      console.error('Error deleting payment:', error)
      showStatusMessage('Failed to delete payment', 'error')
    }
  }

  const filteredPayments = payments.filter((payment) => {
    if (filters.payment_type !== 'all' && payment.payment_type !== filters.payment_type) return false
    if (filters.payment_category !== 'all' && payment.payment_category !== filters.payment_category) return false
    if (filters.payment_method !== 'all' && payment.payment_method !== filters.payment_method) return false
    if (filters.is_reconciled !== 'all') {
      const isReconciled = filters.is_reconciled === 'true'
      if (payment.is_reconciled !== isReconciled) return false
    }
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      if (
        !payment.payment_number.toLowerCase().includes(searchLower) &&
        !(payment.description || '').toLowerCase().includes(searchLower) &&
        !(payment.payment_reference || '').toLowerCase().includes(searchLower)
      ) {
        return false
      }
    }
    return true
  })

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Payments</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage all payments and receipts
          </p>
        </div>
        <button
          onClick={handleCreatePayment}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          New Payment
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Search
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Payment number, description..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Payment Type
            </label>
            <select
              value={filters.payment_type}
              onChange={(e) => setFilters({ ...filters, payment_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Types</option>
              <option value="receipt">Receipt</option>
              <option value="payment">Payment</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category
            </label>
            <select
              value={filters.payment_category}
              onChange={(e) => setFilters({ ...filters, payment_category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Categories</option>
              <option value="vendor">Vendor</option>
              <option value="customer">Customer</option>
              <option value="expense">Expense</option>
              <option value="income">Income</option>
              <option value="refund">Refund</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Payment Method
            </label>
            <select
              value={filters.payment_method}
              onChange={(e) => setFilters({ ...filters, payment_method: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Methods</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="bank">Bank</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="digital_wallet">Digital Wallet</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reconciliation Status
            </label>
            <select
              value={filters.is_reconciled}
              onChange={(e) => setFilters({ ...filters, is_reconciled: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All</option>
              <option value="true">Reconciled</option>
              <option value="false">Not Reconciled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Loading payments...</p>
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="p-8 text-center">
            <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-gray-500 dark:text-gray-400">No payments found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reference Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {payment.payment_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(payment.payment_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPaymentTypeBadge(payment.payment_type)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPaymentCategoryBadge(payment.payment_category)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(payment.total_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 capitalize">
                      {payment.payment_method.replace('_', ' ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {payment.is_reconciled ? (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          Reconciled
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                      {payment.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleViewPayment(payment)}
                          className="text-primary-600 hover:text-primary-900 dark:text-primary-400"
                          title="View"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleEditPayment(payment)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400"
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeletePayment(payment)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400"
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {isEditing ? 'Edit Payment' : selectedPayment ? 'Payment Details' : 'New Payment'}
                </h3>
                <button
                  onClick={() => {
                    setShowPaymentModal(false)
                    setSelectedPayment(null)
                    setIsEditing(false)
                  }}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {selectedPayment && !isEditing ? (
                // View Mode
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Reference Number</label>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedPayment.payment_number}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Payment Date</label>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(selectedPayment.payment_date)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Type</label>
                      <p className="mt-1">{getPaymentTypeBadge(selectedPayment.payment_type)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
                      <p className="mt-1">{getPaymentCategoryBadge(selectedPayment.payment_category)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Amount</label>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatCurrency(selectedPayment.amount)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Tax Amount</label>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatCurrency(selectedPayment.tax_amount)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Total Amount</label>
                      <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(selectedPayment.total_amount)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Payment Method</label>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white capitalize">{selectedPayment.payment_method.replace('_', ' ')}</p>
                    </div>
                    {selectedPayment.payment_reference && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Reference</label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedPayment.payment_reference}</p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                      <p className="mt-1">
                        {selectedPayment.is_reconciled ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            Reconciled
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                            Pending
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  {selectedPayment.description && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedPayment.description}</p>
                    </div>
                  )}
                  {selectedPayment.notes && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedPayment.notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                // Edit/Create Mode
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Payment Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={paymentForm.payment_date}
                        onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Payment Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={paymentForm.payment_type}
                        onChange={(e) => setPaymentForm({ ...paymentForm, payment_type: e.target.value as 'receipt' | 'payment' })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        required
                      >
                        <option value="receipt">Receipt</option>
                        <option value="payment">Payment</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Category <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={paymentForm.payment_category}
                        onChange={(e) => setPaymentForm({ ...paymentForm, payment_category: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        required
                      >
                        <option value="vendor">Vendor</option>
                        <option value="customer">Customer</option>
                        <option value="expense">Expense</option>
                        <option value="income">Income</option>
                        <option value="refund">Refund</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Payment Method <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={paymentForm.payment_method}
                        onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        required
                      >
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="bank">Bank</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="digital_wallet">Digital Wallet</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Amount <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={paymentForm.amount}
                        onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tax Amount
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={paymentForm.tax_amount}
                        onChange={(e) => setPaymentForm({ ...paymentForm, tax_amount: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Payment Reference
                      </label>
                      <input
                        type="text"
                        value={paymentForm.payment_reference}
                        onChange={(e) => setPaymentForm({ ...paymentForm, payment_reference: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Check number, transaction ID..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Source Transaction Type
                      </label>
                      <input
                        type="text"
                        value={paymentForm.source_transaction_type}
                        onChange={(e) => setPaymentForm({ ...paymentForm, source_transaction_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="invoice, expense, order..."
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      value={paymentForm.description}
                      onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      rows={3}
                      placeholder="Payment description..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={paymentForm.notes}
                      onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      rows={3}
                      placeholder="Additional notes..."
                    />
                  </div>
                </div>
              )}
            </div>
            {(!selectedPayment || isEditing) && (
              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowPaymentModal(false)
                    setSelectedPayment(null)
                    setIsEditing(false)
                  }}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePayment}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  {isEditing ? 'Update Payment' : 'Create Payment'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Delete Payment
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete payment <strong>{selectedPayment.payment_number}</strong>? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowDeleteModal(false)
                    setSelectedPayment(null)
                  }}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeletePayment}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                {statusModalType === 'success' ? (
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900">
                    <CheckIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900">
                    <XMarkIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
                  </div>
                )}
              </div>
              <h3 className={`text-lg font-semibold text-center mb-2 ${
                statusModalType === 'success' 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {statusModalType === 'success' ? 'Success' : 'Error'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
                {statusModalMessage}
              </p>
              <div className="flex justify-center">
                <button
                  onClick={() => setShowStatusModal(false)}
                  className={`px-6 py-2 rounded-md font-medium ${
                    statusModalType === 'success'
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-red-600 hover:bg-red-700 text-white'
                  }`}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Payments


